import React from "react";
import { MapContainer, TileLayer } from "react-leaflet";
import { LatLngExpression, LatLngTuple } from "leaflet";
import "leaflet/dist/leaflet.css";
import { MAP_TILE_URL, MAP_TILE_MAX_ZOOM } from "../../config/mapTiles";
import CustomZoomControl from "./components/CustomZoomControl";
import MapAttribution from "./components/MapAttribution";

/** Zoomstufe der Hintergrundkarte bei `smoothFlights` – etwa die der Übersicht über Deutschland */
const BACKDROP_TILE_ZOOM = 6;

interface BaseMapProps {
  /** Kartenmittelpunkt */
  center: LatLngTuple;
  /** Anfangs-Zoomstufe */
  zoom: number;
  /** Kartenhöhe (CSS-Wert oder Pixel) */
  height: number | string;
  /** Zoomen per Mausrad (Standard: an) */
  scrollWheelZoom?: boolean;
  /** Zoomen per Doppelklick (Standard: an) */
  doubleClickZoom?: boolean;
  /** Eigene Zoom-Buttons links oben (Standard: aus) */
  showZoomControl?: boolean;
  /**
   * Für Karten, die per `flyTo` über viele Zoomstufen fliegen (Standard: aus). Darunter liegt
   * dann eine grobe Hintergrundkarte, damit beim Rausfliegen statt Grau Karte um den bisherigen
   * Ausschnitt steht, und die eigentlichen Kacheln laden erst am Ziel – nicht Zoomstufe für
   * Zoomstufe während des Flugs.
   */
  smoothFlights?: boolean;
  /** Ecke für den Attributions-Hinweis (Standard: links) */
  attributionAlign?: "left" | "right";
  /** Layer, Marker und Controls dieser Karte */
  children?: React.ReactNode;
}

/**
 * Kartenrahmen für alle Karten der Anwendung.
 *
 * Besitzt alles, was jede Karte gleich hat: Container-Grundeinstellungen,
 * Kachel-Layer und Attribution. Der Inhalt — Layer, Marker, Controller —
 * kommt über children.
 *
 * Bewusst keine Weitergabe beliebiger MapContainer-Props: Die Attribution ist
 * Bedingung der CARTO-Nutzung und darf von Aufrufern nicht abschaltbar sein.
 */
const BaseMap: React.FC<BaseMapProps> = ({
  center,
  zoom,
  height,
  scrollWheelZoom = true,
  doubleClickZoom = true,
  showZoomControl = false,
  smoothFlights = false,
  attributionAlign = "left",
  children,
}) => (
  <MapContainer
    center={center as LatLngExpression}
    zoom={zoom}
    style={{ height, width: "100%", zIndex: 0 }}
    zoomControl={false}
    attributionControl={false}
    scrollWheelZoom={scrollWheelZoom}
    doubleClickZoom={doubleClickZoom}
  >
    {smoothFlights && (
      // Immer dieselben wenigen Kacheln der groben Stufe, vergrößert – unter der eigentlichen
      // Karte unsichtbar, beim Flug die Fläche, die sonst grau bliebe
      <TileLayer
        url={MAP_TILE_URL}
        maxZoom={MAP_TILE_MAX_ZOOM}
        maxNativeZoom={BACKDROP_TILE_ZOOM}
        zIndex={0}
      />
    )}
    <TileLayer
      url={MAP_TILE_URL}
      maxZoom={MAP_TILE_MAX_ZOOM}
      updateWhenZooming={!smoothFlights}
    />
    {showZoomControl && <CustomZoomControl />}
    {children}
    <MapAttribution align={attributionAlign} />
  </MapContainer>
);

export default BaseMap;
